import { useState, useRef } from "react";
import { toast } from "sonner";
import { Camera, Loader2, UploadCloud, UserCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { v4 as uuidv4 } from 'uuid';

interface ProfileImageUploadProps {
  userId: string;
  existingImageUrl?: string | null;
  userInitials: string;
  onImageUploaded: (url: string) => void;
}

const ProfileImageUpload = ({ 
  userId, 
  existingImageUrl, 
  userInitials, 
  onImageUploaded 
}: ProfileImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(existingImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      // Generate a unique filename
      const filename = `${userId}-${uuidv4()}`;
      const fileExt = file.name.split('.').pop();
      const filePath = `${filename}.${fileExt}`;

      // Upload the file to Supabase storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        if (error.message.includes("not found")) {
          toast.error("Storage bucket not configured. Please create an 'avatars' bucket in Supabase.");
          throw error;
        } else {
          throw error;
        }
      }

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setImageUrl(publicUrl);
      onImageUploaded(publicUrl);
      
      // Update the user_profiles table directly with the new image URL
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ profile_image_url: publicUrl })
        .eq('user_id', userId);
        
      if (updateError) {
        console.error("Error updating profile with image URL:", updateError);
        throw updateError;
      }
      
      toast.success("Profile image uploaded successfully");
    } catch (error: any) {
      console.error("Error uploading image:", error);
      
      if (error.message && error.message.includes("does not exist")) {
        toast.error("Storage not configured. Please try again after the system is set up.");
      } else {
        toast.error("Error uploading image. Please try again.");
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
        data-testid="profile-image-input"
      />
      
      <div className="relative">
        <Avatar className="h-24 w-24 border-2 border-[#CCCCCC] shadow-md cursor-pointer transition-all duration-300 hover:border-[#3BAFDA] hover:shadow-lg">
          {imageUrl ? (
            <AvatarImage src={imageUrl} alt="Profile" className="aspect-square w-full object-cover rounded-full" />
          ) : (
            <AvatarFallback className="bg-gradient-to-br from-[#E8F0F8] to-[#3BAFDA] text-white text-xl font-semibold">
              <UserCircle2 className="h-20 w-20 text-[#3BAFDA]/80" />
            </AvatarFallback>
          )}
        </Avatar>
        
        <Button
          type="button"
          size="icon"
          className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full bg-[#3BAFDA] hover:bg-[#333B44] text-white shadow-md"
          onClick={handleUploadClick}
          disabled={uploading}
        >
          <Camera className="h-4 w-4" />
        </Button>
      </div>
      
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-xs border-[#CCCCCC] hover:bg-[#E8F0F8]"
        onClick={handleUploadClick}
        disabled={uploading}
      >
        {uploading ? (
          <>
            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <UploadCloud className="mr-2 h-3 w-3" />
            {imageUrl ? "Change Photo" : "Upload Photo"}
          </>
        )}
      </Button>
    </div>
  );
};

export default ProfileImageUpload;
